import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { Message } from 'primereact/message';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { useNavigate } from 'react-router-dom';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { toastify } from '../../../../../../utils/toast';
import { GET_FACILITY_INVOICES } from '../../../../../../utils/urls';

const InvoicesTab = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const dt = useRef(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [yearMonthFilter, setYearMonthFilter] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 0 });

    // meter status map: unitId -> liveStatus
    const [meterStatusMap, setMeterStatusMap] = useState({});

    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    const statusOptions = [
        { label: 'All Statuses', value: null },
        { label: 'Paid', value: 'Paid' },
        { label: 'Partially Paid', value: 'Partially Paid' },
        { label: 'Cancelled', value: 'Cancelled' },
        { label: 'Overdue', value: 'Overdue' },
        { label: 'Unpaid', value: 'Unpaid' },
    ];

    const yearMonthOptions = (() => {
        const opts = [{ label: 'All Months', value: null }];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            opts.push({ label: d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }), value: ym });
        }
        return opts;
    })();

    // ─── Fetch invoices ────────────────────────────────────────────────────────
    const fetchInvoices = async () => {
        if (!selectedFacilityId) return;
        setLoading(true);
        try {
            const page = lazyParams.page + 1;
            const queryParams = new URLSearchParams();
            queryParams.append('page', page);
            queryParams.append('limit', lazyParams.rows);
            if (statusFilter)    queryParams.append('status',    statusFilter);
            if (yearMonthFilter) queryParams.append('yearMonth', yearMonthFilter);

            const url = `${GET_FACILITY_INVOICES}/${selectedFacilityId}?${queryParams.toString()}`;
            const response = await makeRequest2(url, 'GET', {});

            if (response.success) {
                const payload      = response.data || {};
                const invoicesList = payload.data || payload.invoices || [];
                const total        = payload.total || payload.totalCount || invoicesList.length;

                setInvoices(invoicesList);
                setTotalRecords(total);

                // Once we have invoices, fetch meter statuses for the unitIds on this page
                fetchMeterStatuses(invoicesList);
            } else {
                toastify(response.message || 'Failed to fetch invoices', 'error');
                setInvoices([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            toastify(error.message || 'Failed to fetch invoices', 'error');
            setInvoices([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // ─── Fetch meter statuses for the current page's unit IDs ─────────────────
    const fetchMeterStatuses = async (invoicesList) => {
        if (!invoicesList || invoicesList.length === 0) return;

        // Deduplicate unitIds
        const uniqueUnitIds = [...new Set(invoicesList.map(inv => inv.unitId).filter(Boolean))];

        // Fetch each meter in parallel — same endpoint used in ViewUnit
        const results = await Promise.allSettled(
            uniqueUnitIds.map(unitId =>
                makeRequest2(
                    `/api/app/power_meter_management/controllers/power_meters/get_unit_meter/${unitId}`,
                    'GET',
                    {}
                )
            )
        );

        const newMap = {};
        results.forEach((result, index) => {
            const unitId = uniqueUnitIds[index];
            if (result.status === 'fulfilled' && result.value?.success && result.value?.data?.data) {
                newMap[unitId] = result.value.data.data.liveStatus || 'unknown';
            } else {
                newMap[unitId] = 'unknown';
            }
        });

        setMeterStatusMap(newMap);
    };

    useEffect(() => {
        if (selectedFacilityId) fetchInvoices();
    }, [selectedFacilityId, statusFilter, yearMonthFilter, lazyParams.page, lazyParams.rows]);

    // ─── Bulk actions: generate invoices / send SMS & email for a month ───────
    const POWER_INVOICING_BASE = '/api/app/power_meter_management/controllers/invoicing';
    const bulkMonthOptions = yearMonthOptions.filter(o => o.value !== null);

    const [bulkType, setBulkType] = useState(null); // 'generate' | 'notify'
    const [bulkMonth, setBulkMonth] = useState(null);
    const [bulkMeterType, setBulkMeterType] = useState(null); // null = all, 'analog', 'smart'
    const [bulkSendSms, setBulkSendSms] = useState(true);
    const [bulkSendEmail, setBulkSendEmail] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    // Same bulk actions as the water invoices tab (power has no overdue-reminder endpoint)
    const bulkActionOptions = [
        { label: 'Generate Invoices', value: 'generate', icon: 'ti ti-receipt' },
        { label: 'Send Notifications', value: 'notify', icon: 'ti ti-bell' },
    ];

    const meterTypeOptions = [
        { label: 'All meters', value: null },
        { label: 'Analog meters only', value: 'analog' },
        { label: 'Smart meters only', value: 'smart' },
    ];

    const openBulk = (type) => {
        setBulkType(type);
        setBulkMonth(yearMonthFilter || bulkMonthOptions[0]?.value || null);
        setBulkMeterType(null);
        setBulkSendSms(true);
        setBulkSendEmail(false);
        setBulkResult(null);
    };

    const closeBulk = () => {
        if (!bulkLoading) setBulkType(null);
    };

    const handleBulkSubmit = async () => {
        if (!bulkMonth) {
            toastify('Please select a month', 'error');
            return;
        }
        if (bulkType === 'notify' && !bulkSendSms && !bulkSendEmail) {
            toastify('Select SMS, email or both', 'error');
            return;
        }

        setBulkLoading(true);
        setBulkResult(null);
        try {
            const isGenerate = bulkType === 'generate';
            const response = await makeRequest2(
                isGenerate
                    ? `${POWER_INVOICING_BASE}/run_facility_billing/${selectedFacilityId}`
                    : `${POWER_INVOICING_BASE}/send_notifications/${selectedFacilityId}`,
                'POST',
                isGenerate
                    ? { yearMonth: bulkMonth, ...(bulkMeterType ? { meterType: bulkMeterType } : {}) }
                    : { yearMonth: bulkMonth, sendSms: bulkSendSms, sendEmail: bulkSendEmail },
                { timeout: 10 * 60 * 1000 } // whole-facility runs can take a while
            );

            if (response.success) {
                setBulkResult(response.data);
                if (isGenerate) {
                    toastify(`${response.data?.created || 0} invoice(s) generated`, response.data?.created ? 'success' : 'info');
                } else {
                    toastify(response.data?.message || 'Notifications queued and sending in the background', response.data?.queued === false ? 'info' : 'success');
                }
                fetchInvoices();
            } else {
                toastify(response.error || 'Request failed', 'error');
            }
        } catch (error) {
            toastify(error.message || 'Request failed', 'error');
        } finally {
            setBulkLoading(false);
        }
    };

    const resetPage = () => setLazyParams(prev => ({ ...prev, first: 0, page: 0 }));

    const handleStatusChange    = (value) => { setStatusFilter(value);    resetPage(); };
    const handleYearMonthChange = (value) => { setYearMonthFilter(value); resetPage(); };
    const handleSearchChange    = (value) => { setSearchTerm(value);      resetPage(); };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter(null);
        setYearMonthFilter(null);
        resetPage();
    };

    const formatKES = (v) =>
        new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

    const handleView = (invoice) => {
        navigate('/facility/utility_management/invoice_management/power_invoice_page', {
            state: { invoiceId: invoice._id, invoiceData: invoice },
        });
    };

    const exportCSV = async () => {
        try {
            setLoading(true);

            const queryParams = new URLSearchParams();
            queryParams.append('page', 1);
            queryParams.append('limit', 1000);
            if (statusFilter)    queryParams.append('status',    statusFilter);
            if (yearMonthFilter) queryParams.append('yearMonth', yearMonthFilter);

            const url = `${GET_FACILITY_INVOICES}/${selectedFacilityId}?${queryParams.toString()}`;
            const response = await makeRequest2(url, 'GET', {});

            if (response.success) {
                const data = response.data?.data || response.data || {};
                const source = Array.isArray(data) ? data : (data.data || data.invoices || []);

                let filteredSource = source;
                if (searchTerm.trim()) {
                    const t = searchTerm.toLowerCase();
                    filteredSource = source.filter(i =>
                        i.invoiceNumber?.toLowerCase().includes(t) ||
                        (i.CustomerInfo?.fullName || '').toLowerCase().includes(t) ||
                        (i.unitName || '').toLowerCase().includes(t)
                    );
                }

                const header = [
                    'Invoice No', 'Date', 'Customer', 'Unit', 'Meter No', 'Tariff',
                    'Units (kWh)', 'Invoice (KES)', 'Paid (KES)', 'Balance (KES)',
                    'Status', 'Due Date', 'Month', 'SMS', 'Email',
                ];

                const rows = filteredSource.map(inv => {
                    const total = inv.powerCharges?.totalCharge || inv.totalAmount || 0;
                    const paid  = inv.amountPaid || 0;
                    const { sms, email } = inv.notificationsSent || {};
                    const usage = inv.meterReadings?.usage ?? inv.consumption;
                    return [
                        inv.invoiceNumber || 'N/A',
                        inv.dateIssued ? new Date(inv.dateIssued).toLocaleDateString() : 'N/A',
                        inv.CustomerInfo?.fullName || 'N/A',
                        inv.unitName || 'N/A',
                        inv.meterNumber || 'N/A',
                        inv.tariff || inv.powerCharges?.tariff || 'N/A',
                        usage != null ? Number(usage).toFixed(2) : '0.00',
                        total,
                        paid,
                        Math.max(0, total - paid),
                        inv.status || 'N/A',
                        inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : 'N/A',
                        inv.yearMonth || 'N/A',
                        sms ? 'Yes' : 'No',
                        email ? 'Yes' : 'No',
                    ];
                });

                const csv = [
                    header.join(','),
                    ...rows.map(r => r.map(c => (typeof c === 'string' && c.includes(',') ? `"${c}"` : c)).join(','))
                ].join('\n');

                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const link = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = link;
                a.download = `Power_Invoices_${new Date().toISOString().slice(0, 10)}.csv`;
                a.style.visibility = 'hidden';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(link);

                toast.current.show({ severity: 'success', summary: 'Exported', detail: 'CSV downloaded successfully', life: 3000 });
            } else {
                throw new Error(response.message || 'Failed to fetch data for export');
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to export CSV', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    // ─── Column templates ──────────────────────────────────────────────────────

    const statusTemplate = (row) => {
        const map = {
            'Paid':           'bg-success text-light',
            'Unpaid':         'bg-danger text-light',
            'Partially Paid': 'bg-warning text-dark',
            'Overdue':        'bg-danger text-light',
            'Cancelled':      'bg-secondary text-light',
        };
        return (
            <span className={`badge ${map[row.status] || 'bg-secondary text-light'}`}>
                {row.status || 'Unknown'}
            </span>
        );
    };

    // ── Meter status column ────────────────────────────────────────────────────
    const meterStatusTemplate = (row) => {
        const status = meterStatusMap[row.unitId];

        if (!status || status === 'unknown') {
            return (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: '0.75rem', color: '#9ca3af'
                }}>
                    <span style={{
                        width: 8, height: 8, borderRadius: '50%',
                        backgroundColor: '#d1d5db', display: 'inline-block'
                    }} />
                    N/A
                </span>
            );
        }

        const isOn = status === 'on';
        const dotColor = isOn ? '#16a34a' : '#dc2626';
        const labelColor = isOn ? '#15803d' : '#b91c1c';
        const bgColor = isOn ? '#dcfce7' : '#fee2e2';

        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '2px 10px', borderRadius: 12,
                backgroundColor: bgColor, fontSize: '0.75rem',
                fontWeight: 600, color: labelColor,
            }}>
                <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    backgroundColor: dotColor, display: 'inline-block'
                }} />
                {isOn ? 'ON' : 'OFF'}
            </span>
        );
    };

    const customerUnitTemplate = (row) => (
        <div>
            <div className="fw-bold">{row.CustomerInfo?.fullName || row.customerName || 'N/A'}</div>
            <div className="small text-muted">{row.unitName || 'N/A'}</div>
        </div>
    );

    const tariffTemplate = (row) => (
        <span style={{
            display: 'inline-block', fontSize: '0.72rem', padding: '2px 8px',
            borderRadius: '12px', background: '#f0f9ff', border: '1px solid #bae6fd',
            color: '#0369a1', fontWeight: 600, whiteSpace: 'nowrap',
        }}>
            {row.tariff || row.powerCharges?.tariff || 'N/A'}
        </span>
    );

    const unitsTemplate = (row) => {
        const usage = row.meterReadings?.usage ?? row.consumption;
        return (
            <span>
                <strong>{usage != null ? Number(usage).toFixed(2) : 'N/A'}</strong>{' '}
                <span className="text-muted" style={{ fontSize: '0.75rem' }}>kWh</span>
            </span>
        );
    };

    const invoiceAmountTemplate = (row) =>
        formatKES(row.powerCharges?.totalCharge || row.totalAmount || 0);

    const amountPaidTemplate = (row) => formatKES(row.amountPaid || 0);

    const balanceTemplate = (row) => {
        const total = row.powerCharges?.totalCharge || row.totalAmount || 0;
        const bal   = Math.max(0, total - (row.amountPaid || 0));
        return (
            <span style={{ color: bal > 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                {formatKES(bal)}
            </span>
        );
    };

    const dateTemplate = (row, field) =>
        row[field] ? new Date(row[field]).toLocaleDateString() : 'N/A';

    const notificationTemplate = (row) => {
        const { sms, email } = row.notificationsSent || {};
        if (sms && email) return <span>Email &amp; SMS</span>;
        if (email)        return <span>Email</span>;
        if (sms)          return <span>SMS</span>;
        return <span className="text-muted">None</span>;
    };

    const actionsTemplate = (row) => (
        <Button
            icon="ti ti-eye"
            className="p-button-rounded p-button-text p-button-info"
            onClick={() => handleView(row)}
            tooltip="View Invoice"
            tooltipOptions={{ position: 'left' }}
        />
    );

    // ─── Client-side search filter ─────────────────────────────────────────────
    const getFilteredInvoices = () => {
        if (!searchTerm.trim()) return invoices;
        const t = searchTerm.toLowerCase();
        return invoices.filter(inv =>
            inv.invoiceNumber?.toLowerCase().includes(t) ||
            (inv.CustomerInfo?.fullName || inv.customerName || '').toLowerCase().includes(t) ||
            (inv.unitName || '').toLowerCase().includes(t) ||
            (inv.accountNumber || '').toLowerCase().includes(t) ||
            (inv.meterNumber || '').toLowerCase().includes(t)
        );
    };

    const filteredInvoices    = getFilteredInvoices();
    const displayInvoices     = filteredInvoices;
    const displayTotalRecords = searchTerm ? filteredInvoices.length : totalRecords;

    // ─── Page-level totals (current rendered rows) ─────────────────────────────
    const pageTotals = displayInvoices.reduce(
        (acc, inv) => {
            const usage = inv.meterReadings?.usage ?? inv.consumption ?? 0;
            const total = inv.powerCharges?.totalCharge || inv.totalAmount || 0;
            const bal   = Math.max(0, total - (inv.amountPaid || 0));
            acc.units   += Number(usage) || 0;
            acc.cost    += Number(total) || 0;
            acc.balance += Number(bal)   || 0;
            return acc;
        },
        { units: 0, cost: 0, balance: 0 }
    );

    const tableFooter = (
        <div style={{
            display: 'flex', gap: 32, padding: '10px 8px',
            fontWeight: 600, fontSize: '0.875rem', flexWrap: 'wrap'
        }}>
            <span>
                Page Total —
            </span>
            <span style={{ color: '#0369a1' }}>
                Consumed: <strong>{pageTotals.units.toFixed(2)} kWh</strong>
            </span>
            <span style={{ color: '#7c3aed' }}>
                Invoice Total: <strong>KES {formatKES(pageTotals.cost)}</strong>
            </span>
            <span style={{ color: pageTotals.balance > 0 ? '#dc2626' : '#16a34a' }}>
                Pending Balance: <strong>KES {formatKES(pageTotals.balance)}</strong>
            </span>
        </div>
    );

    if (!selectedFacilityId) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <i className="ti ti-building fs-1 text-muted d-block mb-2"></i>
                    Please select a facility to view power invoices.
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <Toast ref={toast} />

            <div className="card-body">

                {/* Filters toolbar */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex flex-wrap gap-2">
                        <input
                            className="form-control"
                            style={{ width: 260 }}
                            placeholder="Search invoice, customer, unit…"
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                        <Dropdown
                            value={statusFilter}
                            options={statusOptions}
                            onChange={(e) => handleStatusChange(e.value)}
                            placeholder="Filter by Status"
                            style={{ width: 180 }}
                        />
                        <Dropdown
                            value={yearMonthFilter}
                            options={yearMonthOptions}
                            onChange={(e) => handleYearMonthChange(e.value)}
                            placeholder="Filter by Month"
                            style={{ width: 180 }}
                        />
                        <Button
                            label="Clear"
                            icon="ti ti-x"
                            className="p-button-outlined"
                            onClick={clearFilters}
                        />
                    </div>
                    <div className="d-flex gap-2">
                        <Dropdown
                            value={null}
                            options={bulkActionOptions}
                            onChange={(e) => openBulk(e.value)}
                            placeholder="Bulk Actions"
                            optionLabel="label"
                            className="p-button-info"
                        />
                        <Button
                            label="CSV"
                            icon="ti ti-file-spreadsheet"
                            className="p-button-success"
                            onClick={exportCSV}
                            disabled={loading}
                        />
                    </div>
                </div>

                {/* Data table */}
                <div className="dt-responsive table-responsive">
                    <DataTable
                        ref={dt}
                        value={displayInvoices}
                        emptyMessage="No power invoices found."
                        sortMode="multiple"
                        paginator
                        lazy={!searchTerm}
                        first={lazyParams.first}
                        rows={lazyParams.rows}
                        totalRecords={displayTotalRecords}
                        onPage={(e) => setLazyParams(e)}
                        loading={loading}
                        stripedRows
                        tableStyle={{ minWidth: '60rem' }}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} invoices"
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        footer={tableFooter}
                    >
                        <Column field="invoiceNumber" header="Invoice No" sortable style={{ minWidth: '130px' }} />

                        <Column
                            field="CustomerInfo.fullName"
                            header="Customer / Unit"
                            body={customerUnitTemplate}
                            sortable
                            style={{ minWidth: '160px' }}
                        />

                        {/* ── Meter Status ── */}
                        <Column
                            header="Meter Status"
                            body={meterStatusTemplate}
                            style={{ minWidth: '110px', textAlign: 'center' }}
                        />

                        <Column
                            header="Units (kWh)"
                            body={unitsTemplate}
                            style={{ minWidth: '100px', textAlign: 'center' }}
                        />

                        <Column
                            header="Invoice (KES)"
                            body={invoiceAmountTemplate}
                            style={{ minWidth: '120px', textAlign: 'right' }}
                        />

                        <Column
                            header="Paid (KES)"
                            body={amountPaidTemplate}
                            style={{ minWidth: '110px', textAlign: 'right' }}
                        />

                        <Column
                            header="Balance (KES)"
                            body={balanceTemplate}
                            style={{ minWidth: '120px', textAlign: 'right' }}
                        />

                        <Column
                            field="status"
                            header="Status"
                            body={statusTemplate}
                            sortable
                            style={{ minWidth: '110px' }}
                        />

                        <Column
                            field="dueDate"
                            header="Due Date"
                            body={(r) => dateTemplate(r, 'dueDate')}
                            style={{ minWidth: '100px' }}
                        />

                        <Column
                            field="yearMonth"
                            header="Month"
                            sortable
                            style={{ minWidth: '90px' }}
                        />

                        <Column
                            header="Notification"
                            body={notificationTemplate}
                            style={{ minWidth: '110px' }}
                        />

                        <Column
                            header="Actions"
                            body={actionsTemplate}
                            style={{ minWidth: '70px', textAlign: 'center' }}
                        />
                    </DataTable>
                </div>
            </div>

            {/* Bulk Action Dialog */}
            <Dialog
                visible={bulkType !== null}
                onHide={closeBulk}
                header={bulkType === 'generate' ? 'Generate Invoices' : 'Send Notifications'}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            className="btn btn-outline-secondary"
                            onClick={closeBulk}
                            disabled={bulkLoading}
                        />
                        <Button
                            label="Execute"
                            className="btn btn-primary"
                            onClick={handleBulkSubmit}
                            loading={bulkLoading}
                            disabled={!bulkMonth}
                        />
                    </div>
                }
                style={{ width: '500px' }}
                modal
            >
                <div className="grid p-fluid">
                    <div className="col-12 mb-3">
                        <Message
                            severity="info"
                            text={bulkType === 'generate'
                                ? 'Generate power invoices for all postpaid meters in the selected month'
                                : 'Send invoice notifications to customers for the selected month'}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div className="col-12">
                        <label htmlFor="bulkYearMonth" className="font-bold mb-2">
                            Select Month <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            id="bulkYearMonth"
                            value={bulkMonth}
                            options={bulkMonthOptions}
                            onChange={(e) => setBulkMonth(e.value)}
                            placeholder="Select month"
                        />
                    </div>

                    {bulkType === 'generate' && (
                        <div className="col-12 mt-3">
                            <label htmlFor="bulkMeterType" className="font-bold mb-2">Meters</label>
                            <Dropdown
                                id="bulkMeterType"
                                value={bulkMeterType}
                                options={meterTypeOptions}
                                onChange={(e) => setBulkMeterType(e.value)}
                            />
                        </div>
                    )}

                    {bulkType === 'notify' && (
                        <>
                            <div className="col-12 mt-3">
                                <div className="flex align-items-center mb-2">
                                    <InputSwitch
                                        id="sendSms"
                                        checked={bulkSendSms}
                                        onChange={(e) => setBulkSendSms(e.value)}
                                    />
                                    <label htmlFor="sendSms" className="ml-2 font-bold">Send SMS</label>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="flex align-items-center">
                                    <InputSwitch
                                        id="sendEmail"
                                        checked={bulkSendEmail}
                                        onChange={(e) => setBulkSendEmail(e.value)}
                                    />
                                    <label htmlFor="sendEmail" className="ml-2 font-bold">Send Email</label>
                                </div>
                            </div>
                        </>
                    )}

                    {bulkResult && bulkType === 'generate' && (
                        <div className="col-12 mt-3">
                            <Message
                                severity={bulkResult.created > 0 ? 'success' : 'warn'}
                                text={`Created: ${bulkResult.created} · Skipped: ${bulkResult.skipped} · Errors: ${bulkResult.errors}${bulkResult.reasons ? ` — ${bulkResult.reasons}` : ''}`}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {bulkResult && bulkType === 'notify' && (
                        <div className="col-12 mt-3">
                            <Message
                                severity={bulkResult.queued === false ? 'warn' : 'success'}
                                text={bulkResult.message}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
};

export default InvoicesTab;